import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getTelegramMessages } from '../../reducers/telegram/telegram-action';
import { useSocket } from '../../hooks/useSocket';
import Message from './Message';
import { formatTelegramMessage } from '../../utils/chatUtils';

const PAGE_LIMIT = 30;

const Messages = () => {
  const dispatch = useDispatch();
  const scrollRef = useRef(null);

  const { selectedBotConfigId, selectedContact } = useSelector((state) => state.telegram);

  const [messages, setMessages] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastMessageId, setLastMessageId] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);

  let companyKey = JSON.parse(localStorage.getItem('companykey'));

  const shouldConnectSocket = companyKey?.id && selectedBotConfigId && selectedContact?.chatId;

  const { socket } = useSocket(
    shouldConnectSocket ? companyKey.id : null,
    null,
    null,
    selectedBotConfigId,
    selectedContact?.chatId,
  );

  /* ----------------------------------
     Fetch messages (pagination)
  -----------------------------------*/
  const fetchMessages = async (lastId = 0) => {
    if (!selectedBotConfigId || !selectedContact?.chatId) return;

    setLoading(true);
    setError(null);

    try {
      const res = await dispatch(
        getTelegramMessages({
          botConfigId: selectedBotConfigId,
          chatId: selectedContact.chatId,
          lastMessageId: lastId,
          limit: PAGE_LIMIT,
        }),
      ).unwrap();

      const rows = res?.rows || [];

      const normalized = rows
        .map(formatTelegramMessage)
        .sort((a, b) => a.finalTimestamp - b.finalTimestamp);

      if (lastId === 0) {
        setMessages(normalized);
      } else {
        setMessages((prev) => [...normalized, ...prev]);
      }

      if (normalized.length > 0) {
        setLastMessageId(normalized[0].id);
      }

      setHasMore(normalized.length === PAGE_LIMIT);
    } catch (err) {
      console.error(err);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  /* ----------------------------------
     Initial load / chat change
  -----------------------------------*/
  useEffect(() => {
    if (selectedContact && selectedBotConfigId) {
      fetchMessages(0);
    }
  }, [selectedContact, selectedBotConfigId]);

  /* ----------------------------------
     Socket: new message
  -----------------------------------*/
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (raw) => {
      const msg = formatTelegramMessage(raw);
      setMessages((prev) => [...prev, msg]);

      const atBottom =
        scrollRef.current &&
        scrollRef.current.scrollHeight - scrollRef.current.scrollTop <=
          scrollRef.current.clientHeight + 50;

      if (atBottom) {
        setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 60);
      }
    };

    socket.on('new_message', handleNewMessage);
    return () => socket.off('new_message', handleNewMessage);
  }, [socket]);

  /* ----------------------------------
     Load older messages
  -----------------------------------*/
  const loadOlder = () => {
    if (!hasMore || loading) return;

    const prevHeight = scrollRef.current?.scrollHeight || 0;

    fetchMessages(lastMessageId).finally(() => {
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight - prevHeight;
        }
      }, 80);
    });
  };

  /* ----------------------------------
     Group messages by date
  -----------------------------------*/
  useEffect(() => {
    const map = {};
    const today = new Date().toDateString();

    messages.forEach((m) => {
      const d = new Date(m.finalTimestamp);
      const key =
        d.toDateString() === today
          ? 'Today'
          : `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(
              2,
              '0',
            )}-${d.getFullYear()}`;

      if (!map[key]) map[key] = [];
      map[key].push(m);
    });

    setGrouped(map);

    if (initialLoad) {
      setTimeout(() => {
        scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight);
        setInitialLoad(false);
      }, 120);
    }
  }, [messages]);

  if (!selectedContact) return null;

  const onScroll = () => {
    if (scrollRef.current?.scrollTop === 0) {
      loadOlder();
    }
  };

  return (
    <div className="messages flex-grow-1 overflow-y-auto" ref={scrollRef} onScroll={onScroll}>
      {error && <div className="error-message">{error}</div>}

      {hasMore && !loading && (
        <div className="w-full flex justify-content-center mb-4 mt-3 __line">
          <button className="__loadBtn" onClick={loadOlder}>
            Load History
          </button>
        </div>
      )}

      {loading && (
        <div className="text-center">
          <i className="pi pi-spin pi-spinner" />
        </div>
      )}

      {Object.keys(grouped).map((dateKey) => (
        <div key={dateKey}>
          <div className="w-full flex justify-content-center">
            <span className="__dateView">{dateKey}</span>
          </div>

          {grouped[dateKey].map((msg, idx) => (
            <Message key_id={msg.id} message={msg} />
          ))}
        </div>
      ))}

      {!messages.length && !loading && (
        <div className="text-center mt-3">Start chat to view messages</div>
      )}
    </div>
  );
};

export default Messages;
